import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { useAuth } from './hooks/useAuth';
import { supabase } from './lib/supabase';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import Login from './pages/Login';
import Register from './pages/Register';
import Dashboard from './pages/Dashboard';
import Skeleton from './components/Skeleton';
import Toast from './components/Toast';
import { Tooltip } from 'react-tooltip';

// Create a client
const queryClient = new QueryClient();

// Get base path from environment variable
const basename = import.meta.env.VITE_BASE_PATH || '/';

function App() {
  const { session, loading } = useAuth();
  
  React.useEffect(() => {
    const updateTitle = async () => {
      if (session?.user) {
        const { data: settings } = await supabase
          .from('settings')
          .select('company_name')
          .eq('user_id', session.user.id)
          .maybeSingle();
        
        document.title = settings?.company_name
          ? `${settings.company_name} - Administratie`
          : 'Administratie';
      } else {
        document.title = 'Administratie';
      }
    };
    
    updateTitle();
  }, [session]);

  // Apply dark mode immediately to prevent flash
  React.useEffect(() => {
    document.documentElement.classList.add('dark');
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-100 dark:bg-gray-900 flex items-center justify-center p-4">
        <div className="w-full max-w-sm">
          <Skeleton className="h-12 w-48 mx-auto mb-8" />
          <div className="space-y-4">
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
    <>
      <Toast />
      <Tooltip id="tooltip" className="z-[99999]" />
      <BrowserRouter basename={basename}>
        <Routes>
          <Route
            path="/login"
            element={
              session ? (
                <Navigate to="/" replace />
              ) : (
                <Login />
              )
            }
          />
          <Route
            path="/register"
            element={
              session ? (
                <Navigate to="/" replace />
              ) : (
                <Register />
              )
            }
          />
          <Route
            path="/*"
            element={
              session ? (
                <Dashboard />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />
        </Routes>
      </BrowserRouter>
    </>
    </QueryClientProvider>
  );
}

export default App;